import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

export type DbProfile = {
  id: string;
  organization_id: string;
  role: string;
  full_name: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  password?: string;
  username?: string | null;
  employee_id?: string | null;
  mobile?: string | null;
  phone?: string | null;
  status: string;
  auth_method?: 'email_password' | 'microsoft_sso' | 'both' | string;
  created_at: string;
  azure_object_id?: string | null;
  department?: string | null;
  department_id?: string | null;
  team_id?: string | null;
  job_title?: string | null;
  manager?: string | null;
  office_location?: string | null;
  profile_photo?: string | null;
  account_status?: string | null;
  sync_status?: string | null;
  last_login?: string | null;
  azure_role?: string | null;
  sso_provider?: string | null;
  sso_uid?: string | null;
  must_change_password?: boolean;
};

export function useUsers() {
  const [profiles, setProfiles] = useState<DbProfile[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const sessionRes = await supabase?.auth.getSession();
      const token = sessionRes?.data?.session?.access_token;

      let fetchedProfiles: DbProfile[] | null = null;

      // 1. Primary: query secure backend proxy endpoint (uses service_role on backend, bypassing Postgres RLS)
      if (token) {
        try {
          const res = await fetch('/api/profiles', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const list = await res.json();
            if (Array.isArray(list)) {
              fetchedProfiles = list;
            }
          }
        } catch (apiErr) {
          console.warn('Proxy /api/profiles failed, falling back to direct select:', apiErr);
        }
      }

      // 2. Fallback: direct Supabase select
      if (!fetchedProfiles && supabase) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });
        if (!error && data) {
          fetchedProfiles = data;
        }
      }

      if (fetchedProfiles) {
        setProfiles(fetchedProfiles);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();

    const channelName = `profiles_changes_${Math.random().toString(36).substring(7)}`;
    const channel = supabase!.channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        fetchUsers();
      })
      .subscribe();

    return () => {
      supabase!.removeChannel(channel);
    };
  }, [fetchUsers]);

  const addUser = async (
    organization_id: string, 
    email: string, 
    full_name: string, 
    role: string, 
    extraData?: Partial<DbProfile>
  ) => {
    try {
      const sessionResponse = await supabase!.auth.getSession();
      const token = sessionResponse.data.session?.access_token;
      if (!token) throw new Error('Your admin session is no longer valid. Please sign in again.');

      const authMethod = extraData?.auth_method || 'both';
      const userPassword = extraData?.password || 'ChangeMe@123';
      const cleanEmail = email.trim().toLowerCase();

      const payload = {
        email: cleanEmail,
        password: userPassword,
        full_name,
        fullName: full_name,
        role,
        organization_id,
        organizationId: organization_id,
        team_id: extraData?.team_id || null,
        department_id: extraData?.department_id || null,
        employee_id: extraData?.employee_id || null,
        mobile: extraData?.mobile || extraData?.phone || null,
        username: extraData?.username || cleanEmail.split('@')[0],
        status: extraData?.status || 'Active',
        auth_method: authMethod,
        authMethod,
        email_confirm: true,
        user_metadata: { 
          name: full_name,
          full_name,
          role,
          organization_id,
          must_change_password: true,
          auth_method: authMethod
        }
      };

      // 1. Create user via backend proxy
      let res = await fetch(`/api/supabase-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        // Fallback to /api/users
        res = await fetch(`/api/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }

      const profileData = await res.json();
      if (!res.ok) {
        console.error('User Creation Error:', profileData);
        throw new Error(profileData.error || profileData.msg || profileData.message || 'Failed to create user');
      }

      // Update local state immediately
      setProfiles(prev => {
        const filtered = prev.filter(p => p.id !== profileData.id && p.email?.toLowerCase() !== cleanEmail);
        return [profileData, ...filtered];
      });

      window.dispatchEvent(new CustomEvent('compass_users_changed', { detail: profileData }));
      await fetchUsers();
      return profileData;
    } catch (err: any) {
      console.error('Error adding user:', err);
      throw err;
    }
  };

  const updateUser = async (id: string, updates: Partial<DbProfile>) => {
    try {
      const sessionResponse = await supabase?.auth.getSession();
      const token = sessionResponse?.data?.session?.access_token;

      // 1. Try secure proxy endpoint first
      if (token) {
        let res = await fetch(`/api/profiles/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ id, updates })
        });

        if (!res.ok) {
          res = await fetch(`/api/profiles?id=${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ id, updates })
          });
        }

        if (res.ok) {
          const data = await res.json();
          setProfiles(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
          window.dispatchEvent(new CustomEvent('compass_users_changed', { detail: data }));
          await fetchUsers();
          return data;
        } else {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || errData.message || 'Failed to update user profile');
        }
      }

      // 2. Fallback to direct client update (strip invalid schema columns)
      if (!supabase) throw new Error('Supabase not configured');
      const { auth_method, password, is_active, ...cleanUpdates } = updates as any;
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...cleanUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setProfiles(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
      window.dispatchEvent(new CustomEvent('compass_users_changed', { detail: data }));
      await fetchUsers();
      return data;
    } catch (err: any) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const updateUserStatus = async (id: string, newStatus: string) => {
    return updateUser(id, {
      status: newStatus,
      account_status: newStatus
    });
  };

  const deleteUser = async (id: string) => {
    try {
      const sessionResponse = await supabase?.auth.getSession();
      const token = sessionResponse?.data?.session?.access_token;

      // Try secure proxy first
      if (token) {
        let res = await fetch(`/api/profiles/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!res.ok) {
          res = await fetch(`/api/profiles?id=${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }

        if (res.ok) {
          setProfiles(prev => prev.filter(p => p.id !== id));
          window.dispatchEvent(new CustomEvent('compass_users_changed', { detail: { id, deleted: true } }));
          await fetchUsers();
          return;
        } else {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || errData.message || 'Failed to delete user');
        }
      }

      // Fallback to direct delete
      if (!supabase) throw new Error('Supabase not configured');
      const { error } = await supabase.from('profiles').delete().eq('id', id);
      if (error) throw error;
      setProfiles(prev => prev.filter(p => p.id !== id));
      window.dispatchEvent(new CustomEvent('compass_users_changed', { detail: { id, deleted: true } }));
      await fetchUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      throw err;
    }
  };

  return {
    profiles,
    loading,
    addUser,
    updateUser,
    updateUserStatus,
    deleteUser,
    refresh: fetchUsers
  };
}
